import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../utils/apiConfig';

const FIELD_TYPES = [
  { value: 'text', label: 'Text Input' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'select', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'file', label: 'File Upload' },
  { value: 'signature', label: 'E-Signature' }
];

const CATEGORIES = [
  'onboarding',
  'compliance',
  'performance',
  'termination',
  'general'
];

export default function FormBuilder() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templatesError, setTemplatesError] = useState('');
  const [templates, setTemplates] = useState([]);

  const [formMeta, setFormMeta] = useState({
    name: '',
    slug: '',
    category: 'general',
    description: ''
  });

  const [fields, setFields] = useState([
    {
      id: 'field_1',
      type: 'text',
      label: 'Example Field',
      required: false,
      placeholder: ''
    }
  ]);

  const addField = () => {
    const newId = `field_${fields.length + 1}`;
    setFields([
      ...fields,
      {
        id: newId,
        type: 'text',
        label: `Field ${fields.length + 1}`,
        required: false,
        placeholder: ''
      }
    ]);
  };

  // Load available templates (including existing/seeded forms)
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoadingTemplates(true);
        setTemplatesError('');
        const token = localStorage.getItem('token');
        const resp = await fetch(`${API_URL}/vnext/forms/templates`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.error || 'Failed to load templates');
        setTemplates(data.data || []);
      } catch (e) {
        setTemplatesError(e.message);
      } finally {
        setLoadingTemplates(false);
      }
    };
    fetchTemplates();
  }, []);

  const applyTemplate = (template) => {
    try {
      const schema = template.schema_json || template.schema || {};
      const props = schema.properties || {};

      const inferred = Object.keys(props).map((key) => {
        const p = props[key] || {};
        let type = 'text';
        if (p.type === 'number') type = 'number';
        if (p.format === 'date') type = 'date';
        if (p.format === 'email') type = 'email';
        if (Array.isArray(p.enum)) type = 'select';
        return {
          id: key,
          type,
          label: p.title || key,
          required: Array.isArray(schema.required) ? schema.required.includes(key) : false,
          placeholder: p.description || ''
        };
      });

      setFormMeta({
        name: template.name || schema.title || formMeta.name,
        slug: template.slug || formMeta.slug,
        category: template.category || 'general',
        description: schema.description || ''
      });

      if (inferred.length > 0) setFields(inferred);
      setSuccess('Template loaded into builder');
      setError('');
    } catch (e) {
      setError('Failed to load template');
    }
  };

  const updateField = (index, key, value) => {
    const updated = [...fields];
    updated[index][key] = value;
    setFields(updated);
  };

  const removeField = (index) => {
    setFields(fields.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Validate
      if (!formMeta.name || !formMeta.slug) {
        throw new Error('Form name and slug are required');
      }

      if (fields.length === 0) {
        throw new Error('Add at least one field');
      }

      // Build JSON schema
      const schema_json = {
        title: formMeta.name,
        description: formMeta.description,
        type: 'object',
        properties: {},
        required: []
      };

      fields.forEach(field => {
        schema_json.properties[field.id] = {
          type: field.type === 'number' ? 'number' : 'string',
          title: field.label
        };

        if (field.type === 'date') {
          schema_json.properties[field.id].format = 'date';
        } else if (field.type === 'email') {
          schema_json.properties[field.id].format = 'email';
        }

        if (field.placeholder) {
          schema_json.properties[field.id].description = field.placeholder;
        }

        if (field.required) {
          schema_json.required.push(field.id);
        }
      });

      // Create template
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_URL}/vnext/forms/templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formMeta.name,
          slug: formMeta.slug,
          category: formMeta.category,
          schema_json
        })
      });

      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed to create form');

      setSuccess('Form created successfully!');

      // Redirect after delay
      setTimeout(() => {
        navigate('/v2/forms');
      }, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Form Builder
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Create a custom form - Basic builder (v1)
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Templates Library (read-only preview) */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6">Templates</Typography>
          <Button size="small" onClick={() => navigate('/v2/forms')}>Open Forms v2</Button>
        </Box>
        {templatesError && <Alert severity="error" sx={{ mb: 2 }}>{templatesError}</Alert>}
        {loadingTemplates ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        ) : (
          <Grid container spacing={1}>
            {(templates || []).slice(0, 12).map((t) => (
              <Grid item xs={12} sm={6} md={4} key={t.id}>
                <Box sx={{ p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle2">{t.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{t.category} • v{t.current_version || 1}</Typography>
                  </Box>
                  <Button size="small" variant="outlined" onClick={() => applyTemplate(t)}>Use</Button>
                </Box>
              </Grid>
            ))}
            {templates && templates.length === 0 && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">No templates yet. Use Create Form to add one.</Typography>
              </Grid>
            )}
          </Grid>
        )}
      </Paper>

      {/* Form Metadata */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Form Details
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Form Name"
              value={formMeta.name}
              onChange={(e) => setFormMeta({ ...formMeta, name: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Slug (URL-friendly ID)"
              value={formMeta.slug}
              onChange={(e) => setFormMeta({ ...formMeta, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
              helperText="e.g., employee-handbook-2025"
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={formMeta.category}
                label="Category"
                onChange={(e) => setFormMeta({ ...formMeta, category: e.target.value })}
              >
                {CATEGORIES.map(cat => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={formMeta.description}
              onChange={(e) => setFormMeta({ ...formMeta, description: e.target.value })}
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Fields */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Form Fields
        </Typography>
        {fields.map((field, index) => (
          <Box key={field.id} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Field Label"
                  value={field.label}
                  onChange={(e) => updateField(index, 'label', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Field Type</InputLabel>
                  <Select
                    value={field.type}
                    label="Field Type"
                    onChange={(e) => updateField(index, 'type', e.target.value)}
                  >
                    {FIELD_TYPES.map(ft => (
                      <MenuItem key={ft.value} value={ft.value}>{ft.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Placeholder"
                  value={field.placeholder}
                  onChange={(e) => updateField(index, 'placeholder', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Required?</InputLabel>
                  <Select
                    value={field.required}
                    label="Required?"
                    onChange={(e) => updateField(index, 'required', e.target.value)}
                  >
                    <MenuItem value={false}>No</MenuItem>
                    <MenuItem value={true}>Yes</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Button size="small" color="error" onClick={() => removeField(index)}>
                  Remove Field
                </Button>
              </Grid>
            </Grid>
          </Box>
        ))}
        <Button variant="outlined" onClick={addField}>
          + Add Field
        </Button>
      </Paper>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Form'}
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate('/v2/forms')}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
}
